import { Question, QuestionnairePageEventQuery } from '@/src/graphql/graphql';
import {
    Box,
    Checkbox,
    checkboxClasses,
    FormControl,
    FormLabel,
    Input,
    Radio,
    radioClasses,
    RadioGroup,
    Typography,
} from '@mui/joy';
import { useMediaQuery } from '@mui/material';
import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { StyledButton } from '../common/styled/ActionablesJoy.styled';
import { CartContext } from '../providers/cart';

interface QuestionnaireResponse {
    question: string;
    answer: string;
}

interface QuestionnaireProps {
    event: QuestionnairePageEventQuery['event'];
    questions: Question[];
    organizerPrimaryColor: string;
    setErrorAlert: (error: string | null) => void;
    hasAdditionalTerms: boolean;
}

export default function QuestionnaireLoaded({
    questions,
    organizerPrimaryColor,
    setErrorAlert,
    hasAdditionalTerms,
}: QuestionnaireProps) {
    const history = useHistory();

    const { setQuestionnaireAnswer } = useContext(CartContext);

    const [responses, setResponses] = useState<QuestionnaireResponse[]>(() =>
        questions.map((q) => ({ question: q.text, answer: '' }))
    );

    // Handle Text Inputs
    const handleTextChange = (question: string, answer: string) => {
        if (answer.length <= 400) {
            setResponses((prev) =>
                prev.map((r) => (r.question === question ? { ...r, answer } : r))
            );
        } else {
            setErrorAlert('Answer must be less than 400 characters');
        }
    };

    // Handle Single Choice
    const handleMultipleChoice = (question: string, answer: string) => {
        setResponses((prev) => prev.map((r) => (r.question === question ? { ...r, answer } : r)));
    };

    // Handle Multiple Choice
    const handleMultiSelect = (question: string, option: string) => {
        const response = responses.find((r) => r.question === question);
        const currentSelections = response?.answer
            ? response.answer.split(',').filter((s) => s !== '')
            : [];
        const isSelected = currentSelections.includes(option);

        let newSelections: string[];
        if (isSelected) {
            newSelections = currentSelections.filter((id) => id !== option);
        } else {
            newSelections = [...currentSelections, option];
        }

        setResponses((prev) =>
            prev.map((r) =>
                r.question === question ? { ...r, answer: newSelections.join(',') } : r
            )
        );
    };

    // Filter Questions by Ticket Category (question will have no ticketCategoryIds if it is not specific to a ticket category)

    const validateForm = () => {
        const requiredQuestions = questions.filter((q) => q.isRequired);
        const requiredQuestionsAnswered = requiredQuestions.every((q) => {
            const response = responses.find((r) => r.question === q.text);
            return response?.answer;
        });

        return requiredQuestionsAnswered;
    };

    const handleSubmit = async () => {
        const isValid = validateForm();

        if (!isValid) {
            setErrorAlert('Please answer all required questions');
            return;
        }

        const answers = responses.map((r) => ({ question: r.question, answer: r.answer }));

        setQuestionnaireAnswer(answers);

        if (hasAdditionalTerms) {
            history.push('/additional-terms');
        } else {
            history.push('/payment');
        }
    };

    const inputStyle = {
        '&:focus-within::before': {
            boxShadow: `inset 0px 0px 1px 1px ${organizerPrimaryColor}!important`,
        },
        fontSize: '14px',
    };

    const renderQuestionnaire = (question: Question, index: number) => {
        if (!question || !question.text) {
            console.error('Invalid question at index:', index, question);
            return null;
        }

        try {
            const response = responses.find((r) => r.question === question.text);

            switch (question.type) {
                case 'text':
                    return (
                        <FormControl
                            className="questionnaire-form-control"
                            key={`text-${index}`}
                            required={question.isRequired || false}
                        >
                            <FormLabel>{question.text}</FormLabel>
                            <Input
                                value={response?.answer || ''}
                                onChange={(e) => handleTextChange(question.text, e.target.value)}
                                placeholder={'Answer'}
                                sx={inputStyle}
                            />
                        </FormControl>
                    );

                case 'singleChoice':
                    return (
                        <FormControl
                            key={`single-${index}`}
                            required={question.isRequired || false}
                            className="questionnaire-form-control"
                        >
                            <FormLabel>{question.text}</FormLabel>
                            <RadioGroup
                                value={response?.answer || ''}
                                onChange={(e) =>
                                    handleMultipleChoice(question.text, e.target.value)
                                }
                            >
                                {question.options &&
                                    question.options.map((option, optIndex) =>
                                        option && option.text ? (
                                            <Radio
                                                key={`radio-${index}-${optIndex}`}
                                                value={option.text}
                                                label={option.text}
                                                sx={{
                                                    [`.MuiRadio-radio`]: {
                                                        borderColor: organizerPrimaryColor,
                                                    },
                                                    [`.MuiRadio-radio.${radioClasses.disabled}`]: {
                                                        borderColor: (theme) =>
                                                            theme.palette.neutral[400],
                                                    },
                                                    [`.MuiRadio-radio.${radioClasses.checked} .MuiRadio-icon`]:
                                                        {
                                                            backgroundColor: organizerPrimaryColor,
                                                        },
                                                    fontSize: '14px',
                                                }}
                                            />
                                        ) : null
                                    )}
                            </RadioGroup>
                        </FormControl>
                    );

                case 'multipleChoice':
                    return (
                        <FormControl
                            key={`multi-${index}`}
                            required={question.isRequired || false}
                            className="questionnaire-form-control"
                        >
                            <FormLabel>{question.text}</FormLabel>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {question.options &&
                                    question.options.map((option, optIndex) =>
                                        option && option.text ? (
                                            <Checkbox
                                                key={`checkbox-${index}-${optIndex}`}
                                                checked={
                                                    response?.answer
                                                        ?.split(',')
                                                        .includes(option.text) || false
                                                }
                                                value={option.text}
                                                label={option.text}
                                                onChange={() =>
                                                    handleMultiSelect(question.text, option.text)
                                                }
                                                sx={{
                                                    [`.MuiCheckbox-checkbox`]: {
                                                        borderColor: organizerPrimaryColor,
                                                    },
                                                    [`.MuiCheckbox-checkbox.${checkboxClasses.disabled}`]:
                                                        {
                                                            borderColor: (theme) =>
                                                                theme.palette.neutral[400],
                                                        },
                                                    [`.MuiCheckbox-checkbox.${checkboxClasses.checked}`]:
                                                        {
                                                            backgroundColor: organizerPrimaryColor,
                                                        },
                                                    mb: 1.75,
                                                    fontSize: '14px',
                                                }}
                                            />
                                        ) : null
                                    )}
                            </Box>
                        </FormControl>
                    );

                default:
                    console.warn('Unknown question type:', question.type);
                    return null;
            }
        } catch (error) {
            console.error('Error rendering question:', question, error);
            return (
                <Box key={`error-${index}`}>
                    <Typography>Error rendering question: {question.text || 'Unknown'}</Typography>
                </Box>
            );
        }
    };

    const belowNineHundred = useMediaQuery('(max-width: 900px)');

    return (
        <>
            <Box sx={{ maxWidth: belowNineHundred ? '100%' : '50%' }}>
                <Box
                    sx={{
                        '.questionnaire-form-control': {
                            mb: 3,
                        },
                        '.questionnaire-form-control:last-child': {
                            mb: 0,
                        },
                    }}
                >
                    {questions.map(renderQuestionnaire)}
                </Box>
                <StyledButton
                    onClick={() => {
                        handleSubmit();
                    }}
                    backgroundcolor={organizerPrimaryColor}
                    id={'proceed_to_checkout'}
                    size="lg"
                    sx={{ mt: 3 }}
                >
                    Proceed
                </StyledButton>
            </Box>
        </>
    );
}
